import { FormEvent, useState } from 'react';
import { Button, TextField, Typography } from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import LogoutIcon from '@mui/icons-material/Logout';
import SaveIcon from '@mui/icons-material/Save';
import { useSearchParams } from 'react-router-dom';
import { apiErrorMessage } from 'api/axios_instance';
import useProfile from 'api/auth/useProfile';
import useAuth from 'auth/useAuth';
import { getUserInitials } from 'auth/userDisplay';
import { SectionLabel } from 'components/settingsHub/SectionLabel';
import { Pill } from 'components/indicators/Pill';
import { useKeyboard } from 'hooks/useKeyboard';
import Loader from 'components/Loader';
import { SettingRow } from 'views/settings/SettingsView.styles';
import ChangePasswordSection from './ChangePasswordSection';
import { IdentityCard, IdentityMeta, InitialsAvatar, InlineFieldRow, UsernameText } from './AccountView.styles';

const MAX_DISPLAY_NAME_LENGTH = 150;

const formatDate = (value: string | null) => (value ? new Date(value).toLocaleString() : 'Never');

const AccountView = () => {
  const { user, logout } = useAuth();
  const {
    updateDisplayName: { mutateAsync, isPending },
  } = useProfile();
  const [searchParams, setSearchParams] = useSearchParams();

  const [displayNameDraft, setDisplayNameDraft] = useState<string | null>(null);
  const [displayNameError, setDisplayNameError] = useState<string | null>(null);

  useKeyboard({
    keyDown: (e) => {
      if (e.key === 'Escape' && searchParams.get('config')) {
        setSearchParams({});
      }
    },
  });

  if (!user) return <Loader />;

  const displayName = displayNameDraft ?? user.display_name;
  const trimmedDisplayName = displayName.trim();
  const canSaveDisplayName =
    !!trimmedDisplayName &&
    trimmedDisplayName !== user.display_name &&
    trimmedDisplayName.length <= MAX_DISPLAY_NAME_LENGTH;

  const handleDisplayNameSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!canSaveDisplayName) return;

    setDisplayNameError(null);
    try {
      await mutateAsync({ display_name: trimmedDisplayName });
      setDisplayNameDraft(null);
    } catch (error) {
      setDisplayNameError(apiErrorMessage(error, 'Updating the display name failed.'));
    }
  };

  return (
    <div className="flex max-w-2xl flex-col gap-8">
      <section className="flex flex-col gap-3">
        <SectionLabel>Profile</SectionLabel>
        <IdentityCard>
          <InitialsAvatar aria-hidden>{getUserInitials(user)}</InitialsAvatar>
          <IdentityMeta>
            <Typography sx={{ fontWeight: 700, fontSize: '1.1rem' }} className="truncate">
              {user.display_name}
            </Typography>
            <UsernameText>{user.username}</UsernameText>
            <div className="mt-1 flex flex-wrap items-center gap-1.5">
              <Pill tone={user.is_superuser ? 'primary' : 'neutral'}>{user.is_superuser ? 'Superuser' : 'User'}</Pill>
              {!user.is_active && (
                <Pill tone="error" dot>
                  Inactive
                </Pill>
              )}
            </div>
          </IdentityMeta>
        </IdentityCard>

        <SettingRow>
          <div className="min-w-0">
            <Typography sx={{ fontWeight: 600 }}>Display name</Typography>
            <Typography variant="body2" color="text.secondary">
              The name shown across the app.
            </Typography>
          </div>
          <form onSubmit={(event) => void handleDisplayNameSubmit(event)} noValidate>
            <InlineFieldRow>
              <TextField
                size="small"
                value={displayName}
                onChange={(event) => {
                  setDisplayNameDraft(event.target.value);
                  setDisplayNameError(null);
                }}
                error={!!displayNameError}
                helperText={displayNameError}
                inputProps={{ maxLength: MAX_DISPLAY_NAME_LENGTH, 'aria-label': 'Display name' }}
              />
              <LoadingButton
                type="submit"
                variant="contained"
                startIcon={<SaveIcon />}
                loading={isPending}
                loadingPosition="start"
                disabled={!canSaveDisplayName}
              >
                Save
              </LoadingButton>
            </InlineFieldRow>
          </form>
        </SettingRow>

        <SettingRow>
          <Typography sx={{ fontWeight: 600 }}>Member since</Typography>
          <Typography variant="body2" color="text.secondary">
            {formatDate(user.created_at)}
          </Typography>
        </SettingRow>
        <SettingRow>
          <Typography sx={{ fontWeight: 600 }}>Last sign in</Typography>
          <Typography variant="body2" color="text.secondary">
            {formatDate(user.last_login_at)}
          </Typography>
        </SettingRow>
      </section>

      <section className="flex flex-col gap-3">
        <SectionLabel>Security</SectionLabel>
        <ChangePasswordSection />
      </section>

      <section className="flex flex-col gap-3">
        <SectionLabel>Session</SectionLabel>
        <SettingRow>
          <div>
            <Typography sx={{ fontWeight: 600 }}>Sign out</Typography>
            <Typography variant="body2" color="text.secondary">
              Sign out of this session and return to the login screen.
            </Typography>
          </div>
          <Button variant="outlined" color="error" startIcon={<LogoutIcon />} onClick={() => void logout()}>
            Sign out
          </Button>
        </SettingRow>
      </section>
    </div>
  );
};

export default AccountView;
